import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/useAuth';
import { useAdmin } from '../context/useAdmin';
import { RichText } from './RichText';
import { useQueryNav } from '../lib/nav';
import { locateScreen, visibleRails } from '../lib/rails';
import { api, ApiError, usingMockApi } from '../lib/api';
import { QUICK_QUESTIONS, SESSIONS, answer, greeting } from '../lib/aiMock';
import type { AiMessage } from '../lib/aiMock';
import type { AiConversation, AiSource, AiTurn } from '../lib/types';

/**
 * The assistant that floats over the portal.
 *
 * It is mounted once, above the router, so walking between the dashboard, My
 * Wells and a profile page leaves the conversation exactly where it was —
 * a panel rendered inside a route would be unmounted and forget everything on
 * the first click.
 *
 * It can be dragged anywhere by its header; where it was left is remembered
 * across reloads, and clamped back into view when the window is resized, so a
 * panel parked on a second monitor cannot strand itself off-screen.
 *
 * Answers come from the API, which is the only place the model provider's key
 * exists. When that endpoint is not configured it replies 503, and the panel
 * falls back to the canned answers in lib/aiMock.ts and says which it gave —
 * a demo that keeps working is worth more than one that shows an error, as
 * long as it does not pretend.
 */

const PLACE_KEY = 'wxp.ai.place';
const MARGIN = 16;

const SIZES = {
  normal: { width: 344, height: 468 },
  wide: { width: 620, height: 600 },
};

interface Place {
  right: number;
  bottom: number;
}

const DEFAULT_PLACE: Place = { right: MARGIN, bottom: MARGIN };

function readPlace(): Place {
  try {
    const raw = localStorage.getItem(PLACE_KEY);
    if (!raw) return DEFAULT_PLACE;

    const parsed = JSON.parse(raw) as Partial<Place>;
    if (typeof parsed.right !== 'number' || typeof parsed.bottom !== 'number') {
      return DEFAULT_PLACE;
    }
    return { right: parsed.right, bottom: parsed.bottom };
  } catch {
    return DEFAULT_PLACE;
  }
}

/** Keep the whole panel on screen, whatever it was dragged to before. */
function clamp(place: Place, width: number, height: number): Place {
  const maxRight = Math.max(MARGIN, window.innerWidth - width - MARGIN);
  const maxBottom = Math.max(MARGIN, window.innerHeight - height - MARGIN);

  return {
    right: Math.min(Math.max(MARGIN, place.right), maxRight),
    bottom: Math.min(Math.max(MARGIN, place.bottom), maxBottom),
  };
}

export function AskAi() {
  const { user } = useAuth();
  const { access } = useAdmin();
  const { params } = useQueryNav();
  const navigate = useNavigate();
  const firstName =
    (user?.name ?? '').trim().split(' ')[0] || (user?.email ?? '').split('@')[0] || 'there';

  // The assistant answers about whichever well the portal is showing.
  const wellId = Number(params.get('wellId')) || 344;

  const [open, setOpen] = useState(false);
  const [wide, setWide] = useState(false);
  const [history, setHistory] = useState(false);

  const [messages, setMessages] = useState<AiMessage[]>([]);

  // The stored thread these messages belong to. Zero until the server has
  // recorded one — a deployment with no database never gets past zero, and the
  // panel behaves the same either way.
  const [threadId, setThreadId] = useState(0);

  // The history list, and whether it is everyone's or only this person's.
  const [threads, setThreads] = useState<AiConversation[] | null>(null);
  const [everyone, setEveryone] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);

  const [place, setPlace] = useState<Place>(readPlace);
  const dragging = useRef(false);

  const size = wide ? SIZES.wide : SIZES.normal;
  const thread = useRef<HTMLDivElement | null>(null);
  const nextId = useRef(1);

  // The greeting is part of the conversation rather than decoration, so
  // "New chat" brings it back and the thread is never empty.
  useEffect(() => {
    if (messages.length === 0) setMessages([greeting(firstName)]);
  }, [firstName, messages.length]);

  // Load the history the moment it is asked for, and again after each answer,
  // so a thread started a minute ago is already in the list.
  useEffect(() => {
    if (!history || usingMockApi) return;
    let cancelled = false;

    api
      .aiHistory()
      .then((res) => {
        if (cancelled) return;
        setThreads(res.items);
        setEveryone(res.everyone);
        setHistoryError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setThreads([]);
        setHistoryError(
          err instanceof ApiError && err.status === 503
            ? 'History needs the database — this deployment is not storing conversations.'
            : 'Could not load the history.',
        );
      });

    return () => {
      cancelled = true;
    };
  }, [history, threadId]);

  // Follow the conversation down as it grows.
  useEffect(() => {
    const el = thread.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, thinking, open, wide, history]);

  useEffect(() => {
    const onResize = () => setPlace((p) => clamp(p, size.width, open ? size.height : 48));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [size.width, size.height, open]);

  useEffect(() => {
    try {
      localStorage.setItem(PLACE_KEY, JSON.stringify(place));
    } catch {
      /* private window: the panel simply opens where it always does */
    }
  }, [place]);

  // Open a stored thread: its turns replace what is on screen, and whatever is
  // typed next continues that thread rather than starting a new one.
  const openThread = useCallback(
    async (id: number) => {
      try {
        const t = await api.aiThread(id);

        setMessages([
          greeting(firstName),
          ...(t.messages ?? []).map((m) => ({
            id: nextId.current++,
            from: m.role,
            text: m.content,
          })),
        ]);
        setThreadId(t.id);
        setHistory(false);
      } catch {
        setHistoryError('Could not open that conversation.');
      }
    },
    [firstName],
  );

  // A citation opens the document at the page the excerpt came from. The
  // panel lives above the router, so this works from any screen.
  const openSource = useCallback(
    (src: AiSource) => {
      const where = locateScreen(
        visibleRails(access.layout, access.canOpen),
        'documents',
      );
      const q = new URLSearchParams({
        rail: where?.rail ?? 'Documents',
        ...(where?.tab ? { tab: where.tab } : {}),
        wellId: String(wellId),
        folder: String(src.folderId),
        doc: String(src.documentId),
        at: String(src.page),
      });
      navigate(`/web?${q.toString()}`);
    },
    [navigate, wellId, access],
  );

  const ask = useCallback(
    async (question: string) => {
      const text = question.trim();
      if (!text || thinking) return;

      setHistory(false);
      setDraft('');
      setThinking(true);

      // Build the request from the thread we already have, plus this question.
      //
      // Not from inside a setMessages updater: React runs that later, during
      // the next render, so anything read out of it here is still empty — and
      // the server rightly answered "ask a question first".
      const asked: AiMessage = { id: nextId.current++, from: 'user', text };
      setMessages((prev) => [...prev, asked]);

      // The greeting is ours, not a turn of the conversation, so it does not
      // travel — and the server rejects a conversation not ending in a
      // question.
      const turns: AiTurn[] = [...messages, asked]
        .filter((m) => m.id !== 0)
        .map((m) => ({ role: m.from, content: m.text }));

      try {
        if (usingMockApi) throw new ApiError('mock mode', 503);

        const reply = await api.aiChat(wellId, turns, threadId);
        if (reply.conversationId) setThreadId(reply.conversationId);

        setMessages((prev) => [
          ...prev,
          {
            id: nextId.current++,
            from: 'assistant',
            text: reply.content,
            sources: reply.sources,
            knowledge: reply.knowledge,
          },
        ]);
      } catch (err) {
        // 503 is "not configured" or "the provider is down": both are cases
        // where the canned answer is the better thing to show. Anything else
        // is a real failure and is reported as one.
        const status = err instanceof ApiError ? err.status : 0;

        if (status === 503 || status === 0 || usingMockApi) {
          setMessages((prev) => [
            ...prev,
            { id: nextId.current++, ...answer(text), mocked: true },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              id: nextId.current++,
              from: 'assistant',
              text:
                err instanceof ApiError
                  ? `I could not answer that: ${err.message}`
                  : 'I could not answer that.',
            },
          ]);
        }
      } finally {
        setThinking(false);
      }
    },
    [messages, thinking, wellId, threadId],
  );

  // Drag by the header. Pointer capture keeps the move events coming even when
  // the cursor outruns the panel.
  const startDrag = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;

    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);

    const start = { x: e.clientX, y: e.clientY, ...place };

    const move = (ev: PointerEvent) => {
      if (!dragging.current) return;

      setPlace(
        clamp(
          { right: start.right - (ev.clientX - start.x), bottom: start.bottom - (ev.clientY - start.y) },
          size.width,
          open ? size.height : 48,
        ),
      );
    };

    const stop = () => {
      dragging.current = false;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
  };

  // Signed out, there is no portal to ask about.
  // A role that was not given the assistant does not see its button.
  if (!user || (access.kind === 'role' && !access.canOpen('ai'))) return null;

  const style = { right: place.right, bottom: place.bottom };

  if (!open) {
    return (
      <button type="button" className="aidock" style={style} onClick={() => setOpen(true)}>
        <Sparkle />
        Ask AI
      </button>
    );
  }

  return (
    <section
      className={wide ? 'aipanel aipanel--wide' : 'aipanel'}
      style={{ ...style, width: size.width, height: size.height }}
      aria-label="AI Assistant"
    >
      <header className="aihead" onPointerDown={startDrag}>
        <span className="aihead-title">
          <Sparkle />
          AI Assistant
        </span>

        <span className="aihead-state">
          <i className="aidot" aria-hidden="true" />
          Online · {SESSIONS.length} sessions
        </span>

        <span className="aihead-spacer" />

        <button
          type="button"
          className="aitool"
          title="New chat"
          aria-label="New chat"
          onClick={() => {
            setMessages([greeting(firstName)]);
            setThreadId(0);
            setHistory(false);
          }}
        >
          +
        </button>

        <button
          type="button"
          className={history ? 'aitool is-on' : 'aitool'}
          title="Chat history"
          aria-label="Chat history"
          aria-pressed={history}
          onClick={() => setHistory((h) => !h)}
        >
          <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
            <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <path d="M8 4.5V8l2.5 1.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </button>

        <button
          type="button"
          className="aitool"
          title={wide ? 'Shrink' : 'Expand'}
          aria-label={wide ? 'Shrink' : 'Expand'}
          onClick={() => {
            const next = wide ? SIZES.normal : SIZES.wide;
            setWide((w) => !w);
            setPlace((p) => clamp(p, next.width, next.height));
          }}
        >
          <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
            <path
              d={wide ? 'M6 10 2.5 13.5M10 6 13.5 2.5M6 10h3M6 10v3M10 6H7M10 6V3' : 'M10 6l3.5-3.5M6 10 2.5 13.5M10 2.5h3.5V6M6 13.5H2.5V10'}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <button
          type="button"
          className="aitool"
          title="Close"
          aria-label="Close"
          onClick={() => setOpen(false)}
        >
          ✕
        </button>
      </header>

      {history ? (
        <div className="aihistory">
          <p className="aihistory-band">{everyone ? 'CHAT HISTORY — ALL USERS' : 'CHAT HISTORY'}</p>

          {historyError && <p className="aihistory-note">{historyError}</p>}

          {!usingMockApi && threads === null && !historyError && (
            <p className="aihistory-note">Loading…</p>
          )}

          {!usingMockApi && threads?.length === 0 && !historyError && (
            <p className="aihistory-note">Nothing here yet. Ask something and it will be kept.</p>
          )}

          {(usingMockApi ? [] : (threads ?? [])).map((t) => (
            <button
              key={t.id}
              type="button"
              className="aihistory-row"
              onClick={() => void openThread(t.id)}
            >
              <span className="aihistory-day">
                {formatDay(t.updatedAt)}
                {everyone && t.userEmail ? ` · ${t.userName || t.userEmail}` : ''}
              </span>
              <span className="aihistory-title">{t.title || '(no title)'}</span>
            </button>
          ))}

          {/* Without a server there is nothing stored, so the canned list stands in. */}
          {usingMockApi &&
            SESSIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                className="aihistory-row"
                onClick={() => {
                  setMessages([
                    greeting(firstName),
                    { id: nextId.current++, from: 'user', text: s.title },
                    { id: nextId.current++, ...answer(s.title), mocked: true },
                  ]);
                  setHistory(false);
                }}
              >
                <span className="aihistory-day">{s.day}</span>
                <span className="aihistory-title">{s.title}</span>
              </button>
            ))}
        </div>
      ) : (
        <div className="aithread" ref={thread}>
          {messages.map((m) =>
            m.from === 'user' ? (
              <div key={m.id} className="airow airow--user">
                <p className="aibubble aibubble--user">{m.text}</p>
                <span className="aiface" aria-hidden="true">
                  <svg viewBox="0 0 20 20" width="12" height="12">
                    <circle cx="10" cy="7" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M3.8 17a6.2 6.2 0 0 1 12.4 0" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </span>
              </div>
            ) : (
              <div key={m.id} className="airow">
                <span className="aiface aiface--bot" aria-hidden="true">
                  <Sparkle />
                </span>

                <div className="aibubble">
                  <div className="aibubble-text">
                    <RichText text={m.text} />
                  </div>

                  {m.mocked && <p className="aimocked">Canned answer — the assistant is not connected.</p>}

                  {(() => {
                    const docs = citedSources(m.text, m.sources);
                    return docs.length > 0 ? (
                      <div className="aisources">
                        <span className="aisources-label">Sources</span>
                        {docs.map(({ n, src }) => (
                          <button
                            key={n}
                            type="button"
                            className="aisource"
                            title={`${src.folderName} — open ${src.documentName} at page ${src.page}`}
                            onClick={() => openSource(src)}
                          >
                            <b>[{n}]</b> {src.documentName.replace(/\.pdf$/i, '')} · p.{src.page}
                          </button>
                        ))}
                      </div>
                    ) : null;
                  })()}

                  {(() => {
                    const refs = citedKnowledge(m.text, m.knowledge);
                    return refs.length > 0 ? (
                      <div className="aisources">
                        <span className="aisources-label">Knowledge</span>
                        {refs.map(({ n, ref }) => (
                          <button
                            key={n}
                            type="button"
                            className="aisource aisource--kb"
                            title={`Open the article "${ref.title}"`}
                            onClick={() => navigate(`/web/KnowledgeBase?tab=articles&article=${ref.id}`)}
                          >
                            <b>[K{n}]</b> {ref.title}
                          </button>
                        ))}
                      </div>
                    ) : null;
                  })()}

                  {m.facts && (
                    <dl className="aifacts">
                      {m.facts.map((f) => (
                        <div key={f.label} className="aifact">
                          <dt>{f.label}</dt>
                          <dd className={f.tone ? `aifact-${f.tone}` : undefined}>{f.value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              </div>
            ),
          )}

          {thinking && (
            <div className="airow">
              <span className="aiface aiface--bot" aria-hidden="true">
                <Sparkle />
              </span>
              <p className="aibubble aibubble--typing" role="status">
                <i /> <i /> <i />
              </p>
            </div>
          )}

          {messages.length === 1 && !thinking && (
            <div className="aiquick">
              <p className="aiquick-label">Quick Questions</p>

              {QUICK_QUESTIONS.map((q) => (
                <button key={q} type="button" className="aiquick-item" onClick={() => void ask(q)}>
                  <span>{q}</span>
                  <span aria-hidden="true">›</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <form
        className="aiask"
        onSubmit={(e) => {
          e.preventDefault();
          void ask(draft);
        }}
      >
        <input
          className="aiask-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask a question..."
          aria-label="Ask a question"
        />
        <button type="submit" className="aiask-send" aria-label="Send" disabled={!draft.trim()}>
          <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
            <path d="M3 10.5 17 4l-5 13-2.4-5.2L3 10.5Z" fill="currentColor" />
          </svg>
        </button>
      </form>

      <p className="aifoot">
        Answers come from this well's data only. Conversations are kept and can be read by an
        administrator.
      </p>
    </section>
  );
}

function Sparkle() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" className="aispark">
      <path
        d="M8 1.5 9.3 5.4 13 6.8 9.3 8.2 8 12.1 6.7 8.2 3 6.8l3.7-1.4L8 1.5Z"
        fill="currentColor"
      />
      <path d="M12.8 11.2l.5 1.5 1.4.5-1.4.6-.5 1.5-.6-1.5-1.4-.6 1.4-.5.6-1.5Z" fill="currentColor" />
    </svg>
  );
}

/** "SEP 17" — the shape the portal uses for dates elsewhere. */
function formatDay(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }).toUpperCase();
}

/**
 * The numbers an answer actually cites: [2], [1][3], [1, 3], [1-3].
 *
 * The server sends every excerpt the model was shown, and showing all of them
 * as "sources" was wrong — an answer that says "not in the documents" came
 * with six chips, as if six pages backed it up. A source is something the
 * answer used, and the answer says which by citing it.
 */
function citedNumbers(text: string, prefix: '' | 'K'): number[] {
  const found = new Set<number>();
  const pattern = prefix === 'K' ? /\[K(\d+)\]/gi : /\[(\d+(?:\s*[,\u2013-]\s*\d+)*)\]/g;

  for (const match of text.matchAll(pattern)) {
    for (const part of match[1].split(',')) {
      const range = part.split(/[\u2013-]/).map((x) => Number(x.trim()));
      if (range.length === 2 && range[0] <= range[1] && range[1] - range[0] < 20) {
        for (let n = range[0]; n <= range[1]; n++) found.add(n);
      } else if (Number.isInteger(range[0])) {
        found.add(range[0]);
      }
    }
  }
  return [...found].sort((a, b) => a - b);
}

function citedSources(text: string, sources?: AiSource[]) {
  if (!sources?.length) return [];

  // Keep the model's numbering, so [3] in the text is [3] on the chip; and
  // show a page once even if two of its chunks were cited.
  const seen = new Set<string>();
  const out: { n: number; src: AiSource }[] = [];

  for (const n of citedNumbers(text, '')) {
    const src = sources[n - 1];
    if (!src) continue;

    const key = `${src.documentId}:${src.page}`;
    if (seen.has(key)) continue;
    seen.add(key);

    out.push({ n, src });
  }
  return out;
}

function citedKnowledge(text: string, refs?: { id: number; title: string }[]) {
  if (!refs?.length) return [];

  return citedNumbers(text, 'K')
    .map((n) => ({ n, ref: refs[n - 1] }))
    .filter((c): c is { n: number; ref: { id: number; title: string } } => Boolean(c.ref));
}
